import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Upload,
  Mail,
  User,
  Store,
  Globe,
  Palette,
  Image,
  Sparkles,
  Briefcase,
  Zap,
  Lock,
  X,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface WizardStep {
  id: string;
  title: string;
  subtitle: string;
}

interface WizardData {
  customerEmail: string;
  customerName: string;
  notes: string;
  shopName: string;
  language: string;
  subdomain: string;
  frontDesign: File | null;
  backDesign: File | null;
  frontPlacement: string;
  backPlacement: string;
  collection: string;
}

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const STEPS: WizardStep[] = [
  {
    id: 'customer',
    title: 'Kundendaten',
    subtitle: 'E-Mail und Name für den Zugang',
  },
  {
    id: 'shop',
    title: 'Shop-Basics',
    subtitle: 'Name, Sprache und URL',
  },
  {
    id: 'design',
    title: 'Design Upload',
    subtitle: 'Front und/oder Back hochladen',
  },
  {
    id: 'collection',
    title: 'Kollektion',
    subtitle: 'Theme und Produktauswahl',
  },
  {
    id: 'review',
    title: 'Review & Launch',
    subtitle: 'Prüfen und Shop erstellen',
  },
];

const COLLECTIONS = [
  {
    id: 'fashion',
    icon: Sparkles,
    title: 'Fashion',
    description: 'Trend-Basics, moderne Farben, cleanes Theme.',
    image:
      'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400&h=300&fit=crop',
    products: [
      'Oversized T-Shirts',
      'Cropped Hoodies',
      'Relaxed Fit Pants',
      'Bucket Hats',
    ],
    colors: ['Weiß', 'Schwarz', 'Beige', 'Sage Green', 'Dusty Pink'],
    style: 'Minimalistisch, Clean, Streetwear-inspiriert',
  },
  {
    id: 'business',
    icon: Briefcase,
    title: 'Business',
    description: 'Polos, Hemden-Look, dezente Styles.',
    image:
      'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=400&h=300&fit=crop',
    products: ['Polo-Shirts', 'Oxford Hemden', 'Chinos', 'Softshell Jacken'],
    colors: ['Navy', 'Weiß', 'Hellblau', 'Anthrazit', 'Bordeaux'],
    style: 'Professionell, Klassisch, Corporate Identity',
  },
  {
    id: 'sport',
    icon: Zap,
    title: 'Sport',
    description: 'Funktionsshirts, Performance-Vibes.',
    image:
      'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400&h=300&fit=crop',
    products: [
      'Performance T-Shirts',
      'Running Shorts',
      'Trainingsjacken',
      'Caps',
    ],
    colors: ['Schwarz', 'Neon Gelb', 'Electric Blue', 'Weiß', 'Rot'],
    style: 'Dynamisch, Sportlich, Moisture-Wicking Materialien',
  },
];

const FRONT_PLACEMENTS = [
  {
    id: 'front_center_large',
    label: 'Großer Frontdruck',
    description: 'Mittig, ca. 30×40 cm',
    icon: '📐',
  },
  {
    id: 'front_center_small',
    label: 'Brust mittig (klein)',
    description: 'Oberhalb der Brust, ca. 10×10 cm',
    icon: '🎯',
  },
  {
    id: 'front_left_chest',
    label: 'Linke Brust',
    description: 'Klassischer Logo-Spot, ca. 8×8 cm',
    icon: '📍',
  },
];

const BACK_PLACEMENTS = [
  {
    id: 'back_center_large',
    label: 'Großer Rückendruck',
    description: 'Mittig, ca. 30×40 cm',
    icon: '📐',
  },
  {
    id: 'back_upper',
    label: 'Nacken / Oberer Rücken',
    description: 'Unterhalb des Kragens, ca. 10×10 cm',
    icon: '🔝',
  },
  {
    id: 'back_lower',
    label: 'Unterer Rücken',
    description: 'Oberhalb des Saums, ca. 15×8 cm',
    icon: '⬇️',
  },
];

// ═══════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════

/**
 * Glass card wrapper component with enhanced depth
 */
function GlassCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`
        rounded-section border border-line
        bg-paper-2
        ${className}
      `}
    >
      {children}
    </div>
  );
}

/**
 * Step indicator in sidebar with enhanced contrast
 */
function StepIndicator({
  step,
  index,
  currentStep,
  onClick,
}: {
  step: WizardStep;
  index: number;
  currentStep: number;
  onClick: () => void;
}) {
  const isCompleted = index < currentStep;
  const isActive = index === currentStep;
  const isClickable = index <= currentStep;

  return (
    <button
      onClick={onClick}
      disabled={!isClickable}
      className={`
        flex items-start gap-3 w-full p-3 rounded-card text-left
        transition-all duration-200
        ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}
        ${isActive ? 'bg-primary-100 border-2 border-primary-400' : ''}
        ${isCompleted && !isActive ? 'bg-paper-2 hover:bg-paper-3' : ''}
        ${!isCompleted && !isActive ? 'hover:bg-paper-3' : ''}
      `}
    >
      <div
        className={`
          w-7 h-7 rounded-control flex items-center justify-center text-sm font-bold
          transition-colors duration-200
          ${isCompleted ? 'bg-primary text-primary-fg' : ''}
          ${isActive ? 'bg-primary text-primary-fg' : ''}
          ${!isCompleted && !isActive ? 'bg-paper-3 text-mute border border-line' : ''}
        `}
      >
        {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`font-semibold text-sm ${isActive ? 'text-primary-800' : isCompleted ? 'text-ink' : 'text-mute'}`}
        >
          {step.title}
        </p>
        <p
          className={`text-xs truncate ${isActive ? 'text-primary-600' : 'text-mute'}`}
        >
          {step.subtitle}
        </p>
      </div>
    </button>
  );
}

/**
 * Form field wrapper
 */
function FormField({
  label,
  hint,
  required,
  children,
  className = '',
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-ink">{label}</label>
        {hint && <span className="text-xs text-mute">{hint}</span>}
        {required && !hint && (
          <span className="text-xs text-mute">Pflicht</span>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Text input component with enhanced contrast
 */
function Input({
  icon: Icon,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-mute" />
      )}
      <input
        {...props}
        className={`
          w-full px-4 py-3 rounded-card border border-line
          bg-paper-2
          text-ink placeholder:text-mute
          focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500
          hover:border-line-2
          transition-all duration-200
          ${Icon ? 'pl-11' : ''}
          ${props.className || ''}
        `}
      />
    </div>
  );
}

/**
 * Textarea component with enhanced contrast
 */
function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`
        w-full px-4 py-3 rounded-card border border-line
        bg-paper-2
        text-ink placeholder:text-mute
        focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500
        hover:border-line-2
        transition-all duration-200 resize-none min-h-[100px]
        ${props.className || ''}
      `}
    />
  );
}

/**
 * Select component with enhanced contrast
 */
function Select({
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`
        w-full px-4 py-3 rounded-card border border-line
        bg-paper-2
        text-ink
        focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500
        hover:border-line-2
        transition-all duration-200
        ${props.className || ''}
      `}
    >
      {children}
    </select>
  );
}

/**
 * Placement Modal
 */
function PlacementModal({
  isOpen,
  onClose,
  type,
  placements,
  selectedPlacement,
  onSelect,
  fileName,
}: {
  isOpen: boolean;
  onClose: () => void;
  type: 'front' | 'back';
  placements: typeof FRONT_PLACEMENTS;
  selectedPlacement: string;
  onSelect: (placement: string) => void;
  fileName: string;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-paper-2 rounded-section ring-1 ring-line overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line">
          <h3 className="text-lg font-semibold text-ink">
            {type === 'front'
              ? 'Front-Position wählen'
              : 'Back-Position wählen'}
          </h3>
          <p className="text-sm text-mute mt-1">
            Wo soll <span className="font-medium text-ink">{fileName}</span>{' '}
            platziert werden?
          </p>
        </div>

        {/* Placements */}
        <div className="p-6 space-y-3">
          {placements.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={`
                w-full p-4 rounded-card border text-left
                transition-all duration-200
                ${
                  selectedPlacement === p.id
                    ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-500/20'
                    : 'border-line bg-paper-2 hover:border-line-2 hover:bg-paper-3'
                }
              `}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`
                    w-12 h-12 rounded-card flex items-center justify-center text-xl
                    ${selectedPlacement === p.id ? 'bg-primary-100' : 'bg-paper-2 border border-line'}
                  `}
                >
                  {p.icon}
                </div>
                <div className="flex-1">
                  <p
                    className={`font-semibold ${selectedPlacement === p.id ? 'text-primary-700' : 'text-ink'}`}
                  >
                    {p.label}
                  </p>
                  <p className="text-sm text-mute">{p.description}</p>
                </div>
                {selectedPlacement === p.id && (
                  <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line bg-paper-2 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={onClose}
            disabled={!selectedPlacement}
          >
            Bestätigen
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Design upload card with placement indicator and image preview
 */
function DesignUploadCard({
  type,
  file,
  placement,
  placements,
  onFileSelect,
  onOpenPlacementModal,
  onRemove,
}: {
  type: 'front' | 'back';
  file: File | null;
  placement: string;
  placements: typeof FRONT_PLACEMENTS;
  onFileSelect: (file: File | null) => void;
  onOpenPlacementModal: () => void;
  onRemove: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const placementInfo = placements.find((p) => p.id === placement);

  // Generate preview URL when file changes
  React.useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [file]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
  };

  if (!file) {
    // Empty state - upload prompt
    return (
      <div
        onClick={() => inputRef.current?.click()}
        className="
          p-6 rounded-section border-2 border-dashed border-line 
          bg-paper-3 hover:border-primary-400 hover:bg-primary-50/30
          transition-all duration-200 cursor-pointer
          flex flex-col items-center justify-center text-center
          min-h-[180px]
        "
      >
        <input
          ref={inputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.svg"
          onChange={handleChange}
          className="hidden"
        />
        <div className="w-14 h-14 rounded-section bg-paper-2 border border-line flex items-center justify-center mb-3">
          <Upload className="w-6 h-6 text-mute" />
        </div>
        <p className="font-semibold text-ink">
          {type === 'front' ? 'Front-Design' : 'Back-Design'}
        </p>
        <p className="text-xs text-mute mt-1">PNG, JPG oder SVG • max. 25 MB</p>
      </div>
    );
  }

  // File uploaded state with preview
  return (
    <div
      className={`
        rounded-section border-2 transition-all duration-200 overflow-hidden
        ${placement ? 'border-primary-400 bg-paper-2' : 'border-amber-400 bg-paper-2'}
      `}
    >
      {/* Image Preview */}
      <div className="relative aspect-[4/3] bg-paper-3 overflow-hidden">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`${type} design preview`}
            className="w-full h-full object-contain p-4"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-12 h-12 text-mute" />
          </div>
        )}

        {/* Remove button overlay */}
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 p-2 rounded-control bg-black/60 hover:bg-black/80 text-white transition-colors"
          title="Entfernen"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Type badge */}
        <div className="absolute top-2 left-2 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
          {type === 'front' ? 'Front' : 'Back'}
        </div>
      </div>

      {/* File info & Placement */}
      <div className="p-3 border-t border-line bg-paper-2">
        <p className="text-xs text-mute truncate mb-2" title={file.name}>
          {file.name}
        </p>

        {/* Placement selection */}
        {placement ? (
          <button
            onClick={onOpenPlacementModal}
            className="
              w-full p-2.5 rounded-card bg-paper-2 border border-line
              flex items-center justify-between
              hover:border-primary-400 transition-all
            "
          >
            <div className="flex items-center gap-2">
              <span className="text-base">{placementInfo?.icon}</span>
              <span className="text-sm font-medium text-ink">
                {placementInfo?.label}
              </span>
            </div>
            <span className="text-xs text-primary-600 font-medium">Ändern</span>
          </button>
        ) : (
          <button
            onClick={onOpenPlacementModal}
            className="
              w-full p-2.5 rounded-card bg-amber-100 border border-amber-300
              flex items-center justify-center gap-2
              hover:bg-amber-200 transition-colors
            "
          >
            <Palette className="w-4 h-4 text-amber-700" />
            <span className="text-sm font-semibold text-amber-700">
              Position wählen
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// STEP COMPONENTS
// ═══════════════════════════════════════════════════════════════════

function StepCustomer({
  data,
  onChange,
}: {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField label="Kunden-E-Mail" required>
          <Input
            icon={Mail}
            type="email"
            placeholder="name@firma.de"
            value={data.customerEmail}
            onChange={(e) => onChange({ customerEmail: e.target.value })}
          />
        </FormField>

        <FormField label="Kundenname / Brand" required>
          <Input
            icon={User}
            type="text"
            placeholder="z. B. ACME Merch"
            value={data.customerName}
            onChange={(e) => onChange({ customerName: e.target.value })}
          />
        </FormField>
      </div>

      <FormField label="Kurzbeschreibung" hint="Optional">
        <Textarea
          placeholder="z. B. Teamwear für unser Unternehmen oder Merch für Community…"
          value={data.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
      </FormField>
    </div>
  );
}

function StepShop({
  data,
  onChange,
}: {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}) {
  // Simple subdomain availability simulation
  const checkAvailability = (sub: string) => {
    const taken = ['test', 'shop', 'admin', 'www', 'api'];
    return sub.length > 0 && !taken.includes(sub.toLowerCase());
  };

  const normalizedSubdomain = data.subdomain
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '');

  const isAvailable = checkAvailability(normalizedSubdomain);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField label="Shopname" required>
          <Input
            icon={Store}
            type="text"
            placeholder="z. B. ACME Store"
            value={data.shopName}
            onChange={(e) => onChange({ shopName: e.target.value })}
          />
        </FormField>

        <FormField label="Sprache" hint="Empfohlen">
          <Select
            value={data.language}
            onChange={(e) => onChange({ language: e.target.value })}
          >
            <option value="de">Deutsch</option>
            <option value="en">Englisch</option>
          </Select>
        </FormField>
      </div>

      <FormField label="Shop-URL (Subdomain)" required>
        <div className="flex">
          <div className="relative flex-1">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mute" />
            <input
              type="text"
              placeholder="acme"
              value={data.subdomain}
              onChange={(e) =>
                onChange({
                  subdomain: e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, '-'),
                })
              }
              className="
                w-full pl-10 pr-4 py-3 rounded-l-card border border-r-0 border-line
                bg-paper-2
                text-ink placeholder:text-mute
                focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400
                transition-all duration-200
              "
            />
          </div>
          <div className="px-4 py-3 bg-paper-3 border border-line rounded-r-card text-sm text-mute flex items-center">
            .designdeck.shop
          </div>
        </div>
        {data.subdomain && (
          <div className="flex items-center gap-2 mt-2">
            <span
              className={`
                text-xs px-2 py-1 rounded-full font-medium
                ${isAvailable ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
              `}
            >
              {isAvailable ? '✓ Verfügbar' : '✗ Belegt'}
            </span>
            <span className="text-xs text-mute">
              {normalizedSubdomain}.designdeck.shop
            </span>
          </div>
        )}
      </FormField>
    </div>
  );
}

function StepDesign({
  data,
  onChange,
}: {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}) {
  const [frontModalOpen, setFrontModalOpen] = useState(false);
  const [backModalOpen, setBackModalOpen] = useState(false);
  const [pendingFrontFile, setPendingFrontFile] = useState<File | null>(null);
  const [pendingBackFile, setPendingBackFile] = useState<File | null>(null);

  // Handle front file selection - open modal immediately
  const handleFrontFileSelect = (file: File | null) => {
    if (file) {
      setPendingFrontFile(file);
      onChange({ frontDesign: file });
      setFrontModalOpen(true);
    }
  };

  // Handle back file selection - open modal immediately
  const handleBackFileSelect = (file: File | null) => {
    if (file) {
      setPendingBackFile(file);
      onChange({ backDesign: file });
      setBackModalOpen(true);
    }
  };

  // Remove front design
  const handleRemoveFront = () => {
    onChange({ frontDesign: null, frontPlacement: '' });
    setPendingFrontFile(null);
  };

  // Remove back design
  const handleRemoveBack = () => {
    onChange({ backDesign: null, backPlacement: '' });
    setPendingBackFile(null);
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-mute">
        Lade dein Design hoch und wähle die Position. Du kannst Front, Back oder
        beides hochladen.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DesignUploadCard
          type="front"
          file={data.frontDesign}
          placement={data.frontPlacement}
          placements={FRONT_PLACEMENTS}
          onFileSelect={handleFrontFileSelect}
          onOpenPlacementModal={() => setFrontModalOpen(true)}
          onRemove={handleRemoveFront}
        />
        <DesignUploadCard
          type="back"
          file={data.backDesign}
          placement={data.backPlacement}
          placements={BACK_PLACEMENTS}
          onFileSelect={handleBackFileSelect}
          onOpenPlacementModal={() => setBackModalOpen(true)}
          onRemove={handleRemoveBack}
        />
      </div>

      {/* Info hint */}
      {(data.frontDesign || data.backDesign) &&
        ((!data.frontPlacement && data.frontDesign) ||
          (!data.backPlacement && data.backDesign)) && (
          <div className="flex items-center gap-2 p-3 rounded-control bg-amber-50 border border-amber-200">
            <Palette className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-700">
              Bitte wähle für jedes hochgeladene Design eine Position.
            </p>
          </div>
        )}

      {/* Front Placement Modal */}
      <PlacementModal
        isOpen={frontModalOpen}
        onClose={() => setFrontModalOpen(false)}
        type="front"
        placements={FRONT_PLACEMENTS}
        selectedPlacement={data.frontPlacement}
        onSelect={(placement) => onChange({ frontPlacement: placement })}
        fileName={data.frontDesign?.name || pendingFrontFile?.name || ''}
      />

      {/* Back Placement Modal */}
      <PlacementModal
        isOpen={backModalOpen}
        onClose={() => setBackModalOpen(false)}
        type="back"
        placements={BACK_PLACEMENTS}
        selectedPlacement={data.backPlacement}
        onSelect={(placement) => onChange({ backPlacement: placement })}
        fileName={data.backDesign?.name || pendingBackFile?.name || ''}
      />
    </div>
  );
}

/**
 * Collection detail modal
 */
function CollectionDetailModal({
  collection,
  isOpen,
  onClose,
  onSelect,
  isSelected,
}: {
  collection: (typeof COLLECTIONS)[0] | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect: () => void;
  isSelected: boolean;
}) {
  if (!isOpen || !collection) return null;

  const IconComponent = collection.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-paper-2 rounded-section ring-1 ring-line overflow-hidden">
        {/* Hero Image */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={collection.image}
            alt={collection.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 left-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-card bg-paper-2 flex items-center justify-center">
                <IconComponent className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {collection.title}
                </h3>
                <p className="text-white/80 text-sm">
                  {collection.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Products */}
          <div>
            <h4 className="text-sm font-semibold text-mute uppercase tracking-wide mb-3">
              Enthaltene Produkte
            </h4>
            <div className="flex flex-wrap gap-2">
              {collection.products.map((product) => (
                <span
                  key={product}
                  className="px-3 py-1.5 rounded-full bg-paper-3 text-sm font-medium text-ink border border-line"
                >
                  {product}
                </span>
              ))}
            </div>
          </div>

          {/* Colors */}
          <div>
            <h4 className="text-sm font-semibold text-mute uppercase tracking-wide mb-3">
              Verfügbare Farben
            </h4>
            <div className="flex flex-wrap gap-2">
              {collection.colors.map((color) => (
                <span
                  key={color}
                  className="px-3 py-1.5 rounded-full bg-primary-50 text-sm font-medium text-primary-700 border border-primary-200"
                >
                  {color}
                </span>
              ))}
            </div>
          </div>

          {/* Style */}
          <div>
            <h4 className="text-sm font-semibold text-mute uppercase tracking-wide mb-2">
              Style & Theme
            </h4>
            <p className="text-ink">{collection.style}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line bg-paper-2 flex justify-between items-center">
          <p className="text-sm text-mute">
            {isSelected ? '✓ Aktuell ausgewählt' : 'Noch nicht ausgewählt'}
          </p>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose}>
              Schließen
            </Button>
            <Button variant="primary" onClick={onSelect}>
              {isSelected ? 'Ausgewählt' : 'Auswählen'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Collection card with image
 */
function CollectionCard({
  collection,
  selected,
  onSelect,
  onShowDetails,
}: {
  collection: (typeof COLLECTIONS)[0];
  selected: boolean;
  onSelect: () => void;
  onShowDetails: () => void;
}) {
  const IconComponent = collection.icon;

  return (
    <div
      className={`
        rounded-section border-2 overflow-hidden transition-all duration-200
        ${selected ? 'border-primary-500 ring-2 ring-primary-500/20' : 'border-line hover:border-line-2'}
      `}
    >
      {/* Image */}
      <div
        className="relative h-32 overflow-hidden cursor-pointer"
        onClick={onSelect}
      >
        <img
          src={collection.image}
          alt={collection.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Selected badge */}
        {selected && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
            <Check className="w-4 h-4 text-primary-fg" />
          </div>
        )}

        {/* Title overlay */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <div className="w-8 h-8 rounded-control bg-paper-2 flex items-center justify-center">
            <IconComponent className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-white">{collection.title}</span>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 bg-paper-2">
        <p className="text-xs text-mute mb-3 line-clamp-2">
          {collection.description}
        </p>
        <div className="flex items-center justify-between">
          <button
            onClick={onShowDetails}
            className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            <Eye className="w-3.5 h-3.5" />
            Details
          </button>
          <button
            onClick={onSelect}
            className={`
              px-3 py-1.5 rounded-control text-xs font-semibold transition-colors
              ${
                selected
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-paper-3 text-ink-2 hover:bg-primary-50 hover:text-primary-600'
              }
            `}
          >
            {selected ? '✓ Gewählt' : 'Wählen'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepCollection({
  data,
  onChange,
}: {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}) {
  const [detailCollection, setDetailCollection] = useState<
    (typeof COLLECTIONS)[0] | null
  >(null);

  return (
    <div className="space-y-5">
      <p className="text-sm text-ink-2">
        Wähle eine Kollektion für deinen Shop. Jede Kollektion hat ein passendes
        Theme, Farbpalette und Produktauswahl.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLLECTIONS.map((c) => (
          <CollectionCard
            key={c.id}
            collection={c}
            selected={data.collection === c.id}
            onSelect={() => onChange({ collection: c.id })}
            onShowDetails={() => setDetailCollection(c)}
          />
        ))}
      </div>

      {/* Detail Modal */}
      <CollectionDetailModal
        collection={detailCollection}
        isOpen={!!detailCollection}
        onClose={() => setDetailCollection(null)}
        onSelect={() => {
          if (detailCollection) {
            onChange({ collection: detailCollection.id });
            setDetailCollection(null);
          }
        }}
        isSelected={detailCollection?.id === data.collection}
      />
    </div>
  );
}

function StepReview({ data }: { data: WizardData }) {
  const frontPlacementInfo = FRONT_PLACEMENTS.find(
    (p) => p.id === data.frontPlacement
  );
  const backPlacementInfo = BACK_PLACEMENTS.find(
    (p) => p.id === data.backPlacement
  );

  const reviewItems = [
    { label: 'Kunden-E-Mail', value: data.customerEmail || '—' },
    { label: 'Kundenname', value: data.customerName || '—' },
    { label: 'Shopname', value: data.shopName || '—' },
    {
      label: 'Sprache',
      value: data.language === 'en' ? 'Englisch' : 'Deutsch',
    },
    {
      label: 'Shop-URL',
      value: data.subdomain ? `${data.subdomain}.designdeck.shop` : '—',
    },
    {
      label: 'Kollektion',
      value: COLLECTIONS.find((c) => c.id === data.collection)?.title || '—',
    },
    {
      label: 'Front-Design',
      value: data.frontDesign
        ? `${data.frontDesign.name} (${frontPlacementInfo?.label || '—'})`
        : '—',
    },
    {
      label: 'Back-Design',
      value: data.backDesign
        ? `${data.backDesign.name} (${backPlacementInfo?.label || '—'})`
        : '—',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="p-5 rounded-card bg-paper-2 border border-line">
        <h3 className="font-semibold text-ink mb-4">Zusammenfassung</h3>
        <dl className="space-y-3">
          {reviewItems.map((item) => (
            <div key={item.label} className="flex justify-between text-sm">
              <dt className="text-mute">{item.label}</dt>
              <dd className="font-medium text-ink text-right max-w-[200px] truncate">
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="p-5 rounded-card bg-paper-2 border border-line">
        <h3 className="font-semibold text-ink mb-4">
          Was passiert als Nächstes?
        </h3>
        <ol className="space-y-3 text-sm">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
              1
            </span>
            <div>
              <p className="font-medium text-ink">Shop wird angelegt</p>
              <p className="text-mute">Automatisch in wenigen Sekunden</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
              2
            </span>
            <div>
              <p className="font-medium text-ink">Produkte & Theme</p>
              <p className="text-mute">Preset je Kollektion</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
              3
            </span>
            <div>
              <p className="font-medium text-ink">Zugang per E-Mail</p>
              <p className="text-mute">
                An {data.customerEmail || 'den Kunden'}
              </p>
            </div>
          </li>
        </ol>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function ShopWizardPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<WizardData>({
    customerEmail: '',
    customerName: '',
    notes: '',
    shopName: '',
    language: 'de',
    subdomain: '',
    frontDesign: null,
    backDesign: null,
    frontPlacement: '',
    backPlacement: '',
    collection: '',
  });

  const updateData = useCallback((updates: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Validation per step
  const isStepValid = useCallback(
    (step: number): boolean => {
      switch (step) {
        case 0:
          return !!(data.customerEmail.trim() && data.customerName.trim());
        case 1:
          return !!(data.shopName.trim() && data.subdomain.trim());
        case 2: {
          // At least one design with its placement
          const hasFront = data.frontDesign && data.frontPlacement;
          const hasBack = data.backDesign && data.backPlacement;
          return !!(hasFront || hasBack);
        }
        case 3:
          return !!data.collection;
        case 4:
          return true;
        default:
          return false;
      }
    },
    [data]
  );

  const canProceed = isStepValid(currentStep);
  const isLastStep = currentStep === STEPS.length - 1;
  const progress = Math.round((currentStep / (STEPS.length - 1)) * 100);

  const handleNext = () => {
    if (isLastStep) {
      // Submit logic would go here
      alert('Shop wird erstellt... (Demo)');
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <StepCustomer data={data} onChange={updateData} />;
      case 1:
        return <StepShop data={data} onChange={updateData} />;
      case 2:
        return <StepDesign data={data} onChange={updateData} />;
      case 3:
        return <StepCollection data={data} onChange={updateData} />;
      case 4:
        return <StepReview data={data} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      {/* Enhanced gradient orbs for depth */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-300/25 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-primary-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-primary-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <GlassCard className="mb-6">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 text-ink-2 hover:text-ink transition-colors"
              >
                <div className="w-8 h-8 rounded-control bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  <span className="text-sm font-bold text-primary-fg">DD</span>
                </div>
              </Link>
              <div>
                <h1 className="font-semibold text-ink">Shop Setup Wizard</h1>
                <p className="text-sm text-mute">
                  In 2–3 Minuten zum ersten Shop
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-paper-3 text-xs text-ink-2">
                <Lock className="w-3 h-3" />
                Uploads verschlüsselt
              </span>
            </div>
          </div>
        </GlassCard>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-8 lg:self-start">
            <GlassCard className="p-4">
              <div className="mb-4">
                <p className="font-semibold text-ink">Setup-Schritte</p>
                <p className="text-sm text-mute mt-1">
                  Nur das Wichtigste. Alles Weitere später im Admin.
                </p>
              </div>

              <div className="space-y-1">
                {STEPS.map((step, index) => (
                  <StepIndicator
                    key={step.id}
                    step={step}
                    index={index}
                    currentStep={currentStep}
                    onClick={() => index < currentStep && setCurrentStep(index)}
                  />
                ))}
              </div>
            </GlassCard>
          </aside>

          {/* Main Content */}
          <main>
            <GlassCard>
              {/* Header */}
              <div className="px-6 py-5 border-b border-line bg-paper-2 flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-ink">
                    {STEPS[currentStep]?.title}
                  </h2>
                  <p className="text-sm text-ink-2 mt-1">
                    {STEPS[currentStep]?.subtitle}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-ink-2 mb-2">
                    {progress}% abgeschlossen
                  </p>
                  <div className="w-36 h-2.5 bg-paper-3 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">{renderStepContent()}</div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-line bg-paper-3 rounded-b-section flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm text-ink-2">
                  <span className="px-3 py-1.5 rounded-full bg-paper-2 border border-line text-xs font-medium">
                    Schritt {currentStep + 1} von {STEPS.length}
                  </span>
                  <span className="hidden sm:inline text-mute">
                    Du kannst später alles ändern.
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {currentStep > 0 && (
                    <Button variant="outline" onClick={handleBack}>
                      <ChevronLeft className="w-4 h-4" />
                      Zurück
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleNext}
                    disabled={!canProceed}
                  >
                    {isLastStep ? 'Shop erstellen' : 'Weiter'}
                    {!isLastStep && <ChevronRight className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </GlassCard>
          </main>
        </div>
      </div>
    </div>
  );
}
